import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #connectionVersion = 0;
  #initialized = false;
  #navigatingToProduct = false;
  #restorePendingRender = false;
  #initializationObserver = new MutationObserver(() => {
    const collectionGrid = this.querySelector('[ref="grid"]');
    if (this.isConnected && !this.#navigatingToProduct && (!collectionGrid || collectionGrid.hasAttribute('data-availability-sorted'))) {
      this.removeAttribute('aria-busy');
      this.#initialize();
    }
  });

  connectedCallback() {
    this.#navigatingToProduct = false;
    this.#restorePendingRender = false;
    this.addEventListener('click', this.#handleProductNavigation);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.#prepareResults();
  }

  async #prepareResults() {
    const connectionVersion = ++this.#connectionVersion;
    const collectionGrid = this.querySelector('[ref="grid"]');
    if (
      this.hasAttribute('data-available-first') &&
      !collectionGrid?.hasAttribute('data-availability-sorted') &&
      Number(collectionGrid?.dataset.lastPage) > 1
    ) {
      this.setAttribute('aria-busy', 'true');
      this.#initializationObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-availability-sorted'],
      });
      try {
        await sectionRenderer.renderSection(this.sectionId);
      } catch (error) {
        console.error('Unable to sort collection by availability', error);
      } finally {
        if (connectionVersion === this.#connectionVersion && this.isConnected && !this.#navigatingToProduct) {
          this.removeAttribute('aria-busy');
        }
      }
      if (connectionVersion !== this.#connectionVersion || !this.isConnected || this.#navigatingToProduct) return;
    }

    this.#initialize();
  }

  #initialize() {
    if (this.#initialized) return;
    this.#initialized = true;
    this.#initializationObserver.disconnect();
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    ++this.#connectionVersion;
    this.#initialized = false;
    this.#initializationObserver.disconnect();
    this.removeEventListener('click', this.#handleProductNavigation);
    window.removeEventListener('pageshow', this.#handlePageShow);
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #handleProductNavigation = (event) => {
    if (
      event.defaultPrevented ||
      !(event.target instanceof Element) ||
      event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
      event.target.closest('button, input, label, select, [tabindex="1"]')
    ) return;

    const card = event.target.closest('product-card');
    if (!card || card.hasAttribute('data-no-navigation')) return;
    const link = event.target.closest('a[href]') ?? card.querySelector('[ref="productCardLink"]');
    if (!(link instanceof HTMLAnchorElement) || link.target === '_blank' || link.hasAttribute('download')) return;
    if (link.origin !== location.origin || !link.pathname.includes('/products/')) return;

    this.#navigatingToProduct = true;
    this.#restorePendingRender = sectionRenderer.cancelRender(this.sectionId) || this.#restorePendingRender;
  };

  #handlePageShow = async (event) => {
    if (event.persisted && this.#navigatingToProduct) {
      this.#navigatingToProduct = false;
      if (this.#restorePendingRender) {
        this.#restorePendingRender = false;
        try {
          await sectionRenderer.renderSection(this.sectionId);
        } catch (error) {
          console.error('Unable to restore collection results', error);
        }
      }
      if (this.isConnected && !this.#navigatingToProduct) this.#prepareResults();
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
